import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useUser } from "@clerk/clerk-expo";
import { useAuthProvider } from "@helpers/auth-provider";
import useAuthUser from "@hooks/useAuthUser";
import type { UserInfo } from "@memorang/types";
import { fetchUserInfo } from "api/commerce/user-service";

type AuthContextValue = {
	setUserInfoNew: (
		latestFirstName?: string,
		latestLastName?: string,
	) => Promise<void>;
	viewerId?: string;
	newUserInfo?: UserInfo;
	loading?: boolean;
};

const initialContextValue: AuthContextValue = {
	viewerId: undefined,
	setUserInfoNew: () => {
		throw new Error("setUserInfoNew is not implemented");
	},
};

const AuthContext = React.createContext<AuthContextValue>(initialContextValue);

const ClerkAuthProvider = ({
	children,
	identifyUser,
	app,
	appId,
}: {
	children: React.ReactNode;
	appId: string;
	identifyUser: ({
		userId,
		email,
		username,
		app,
	}: { userId: string; email: string; username: string; app: string }) => void;
	app: string;
}) => {
	const [newUserInfo, setNewUserInfo] = useState<UserInfo | undefined>(
		undefined,
	);
	const { user, isLoaded, isSignedIn } = useUser();

	const userId = user?.publicMetadata?.logicalId as string;
	const email = user?.emailAddresses[0].emailAddress;
	const username = user?.username;

	const refreshAuth = useCallback(() => {
		if (isLoaded && isSignedIn && userId && email) {
			identifyUser({ userId, email, username: username || "", app });
		}
	}, [userId, email, username, app, identifyUser, isSignedIn, isLoaded]);

	const setUserInfoNew = useCallback(
		async (latestFirstName?: string, latestLastName?: string) => {
			if (userId && appId && user) {
				if (latestFirstName) {
					setNewUserInfo((prev) => {
						if (!prev) {
							return undefined;
						}
						return {
							...prev,
							firstName: latestFirstName,
							lastName: latestLastName,
						};
					});
					await user.update({
						firstName: latestFirstName,
						lastName: latestLastName,
					});
				} else {
					const result = await fetchUserInfo({ appId });
					setNewUserInfo(result);
				}
			}
		},
		[user, appId, userId],
	);

	useEffect(() => {
		setUserInfoNew();
	}, [setUserInfoNew]);
	useEffect(() => {
		refreshAuth();
	}, [refreshAuth]);

	const contextValue = useMemo(() => {
		return {
			isAuthenticated: !!user,
			user,
			viewerId: user
				? (user.publicMetadata?.logicalId as string | undefined)
				: "",
			loading: !isLoaded,
			setUserInfoNew,
			newUserInfo,
		};
	}, [user, isLoaded, setUserInfoNew, newUserInfo]);

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

const SupabaseAuthProvider = ({
	children,
	identifyUser,
	app,
	appId,
}: {
	children: React.ReactNode;
	appId: string;
	identifyUser: ({
		userId,
		email,
		username,
		app,
	}: { userId: string; email: string; username: string; app: string }) => void;
	app: string;
}) => {
	const [newUserInfo, setNewUserInfo] = useState<UserInfo | undefined>(
		undefined,
	);
	const { email, userId, isLoaded, isSignedIn, userLogicalId } = useAuthUser();

	const viewerId = userLogicalId ?? userId;

	const refreshAuth = useCallback(() => {
		if (isLoaded && isSignedIn && viewerId && email) {
			identifyUser({
				userId: viewerId,
				email,
				username: (email ?? "").split("@")[0],
				app,
			});
		}
	}, [viewerId, email, app, identifyUser, isSignedIn, isLoaded]);

	const setUserInfoNew = useCallback(
		async (latestFirstName?: string, latestLastName?: string) => {
			if (viewerId && appId) {
				if (latestFirstName) {
					setNewUserInfo((prev) => {
						if (!prev) {
							return undefined;
						}
						return {
							...prev,
							firstName: latestFirstName,
							lastName: latestLastName,
						};
					});
				} else {
					const result = await fetchUserInfo({ appId });
					setNewUserInfo(result);
				}
			}
		},
		[appId, viewerId],
	);

	useEffect(() => {
		setUserInfoNew();
	}, [setUserInfoNew]);
	useEffect(() => {
		refreshAuth();
	}, [refreshAuth]);

	const contextValue = useMemo(() => {
		return {
			isAuthenticated: isSignedIn ?? false,
			viewerId: viewerId ?? "",
			loading: !isLoaded,
			setUserInfoNew,
			newUserInfo,
		};
	}, [isSignedIn, viewerId, isLoaded, setUserInfoNew, newUserInfo]);

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

const AuthProvider = (props: {
	children: React.ReactNode;
	appId: string;
	identifyUser: ({
		userId,
		email,
		username,
		app,
	}: { userId: string; email: string; username: string; app: string }) => void;
	app: string;
}) => {
	const { isClerk } = useAuthProvider();
	if (isClerk) {
		return <ClerkAuthProvider {...props} />;
	}
	return <SupabaseAuthProvider {...props} />;
};

const useAuthContext = () => {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuthContext must be used within a AuthProvider");
	}
	return context;
};
export { AuthProvider, useAuthContext };
